import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';

const GENESIS_SCRIPTSIG_HEX =
  '04ffff001d0104455468652054696d65732030332f4a616e2f32303039204368616e63656c6c6f72206f6e206272696e6b206f66207365636f6e64206261696c6f757420666f722062616e6b73';

interface DecodedStep {
  label: string;
  hex: string;
  ascii: string;
}

function decodeScriptSig(hex: string): DecodedStep[] {
  const cleaned = hex.replace(/\s+/g, '');
  if (!/^[0-9a-fA-F]*$/.test(cleaned)) {
    return [];
  }

  const bytes = new Uint8Array(
    cleaned.match(/.{2}/g)?.map((b) => parseInt(b, 16)) ?? [],
  );

  const steps: DecodedStep[] = [];
  let i = 0;

  while (i < bytes.length) {
    const op = bytes[i];

    if (op === 0) {
      // OP_0
      steps.push({ label: 'OP_0', hex: '', ascii: '' });
      i++;
    } else if (op <= 0x4b) {
      // Push next op bytes
      const len = op;
      const data = bytes.slice(i + 1, i + 1 + len);
      const hexStr = Array.from(data)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      const asciiStr = Array.from(data)
        .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : '.'))
        .join('');

      steps.push({
        label: `PUSH ${len} byte${len !== 1 ? 's' : ''}`,
        hex: hexStr,
        ascii: asciiStr,
      });
      i += 1 + len;
    } else if (op === 0x4c) {
      // OP_PUSHDATA1
      const len = bytes[i + 1];
      const data = bytes.slice(i + 2, i + 2 + len);
      const hexStr = Array.from(data)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      const asciiStr = Array.from(data)
        .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : '.'))
        .join('');

      steps.push({
        label: `OP_PUSHDATA1 (${len} byte${len !== 1 ? 's' : ''})`,
        hex: hexStr,
        ascii: asciiStr,
      });
      i += 2 + len;
    } else if (op === 0x4d) {
      // OP_PUSHDATA2
      const len = bytes[i + 1] | (bytes[i + 2] << 8);
      const data = bytes.slice(i + 3, i + 3 + len);
      const hexStr = Array.from(data)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      const asciiStr = Array.from(data)
        .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : '.'))
        .join('');

      steps.push({
        label: `OP_PUSHDATA2 (${len} bytes)`,
        hex: hexStr,
        ascii: asciiStr,
      });
      i += 3 + len;
    } else if (op === 0x4e) {
      // OP_PUSHDATA4
      const len =
        bytes[i + 1] |
        (bytes[i + 2] << 8) |
        (bytes[i + 3] << 16) |
        (bytes[i + 4] << 24);
      const data = bytes.slice(i + 5, i + 5 + len);
      const hexStr = Array.from(data)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      const asciiStr = Array.from(data)
        .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : '.'))
        .join('');

      steps.push({
        label: `OP_PUSHDATA4 (${len} bytes)`,
        hex: hexStr,
        ascii: asciiStr,
      });
      i += 5 + len;
    } else {
      // OP_RETURN or other opcode - treat as unknown
      steps.push({
        label: `OP_0x${op.toString(16).padStart(2, '0')}`,
        hex: '',
        ascii: '',
      });
      i++;
    }
  }

  return steps;
}

function hexToAscii(hex: string): string {
  return (
    hex
      .match(/.{2}/g)
      ?.map((b) => String.fromCharCode(parseInt(b, 16)))
      .join('') ?? ''
  );
}

const Index = () => {
  useSeoMeta({
    title: 'Fun With Freedom Tech Decoder',
    description:
      'Decode the Bitcoin Genesis block scriptSig and uncover Satoshi Nakamoto\'s message. "The Times 03/Jan/2009 Chancellor on brink of second bailout for banks."',
  });

  const [hexInput, setHexInput] = useState(GENESIS_SCRIPTSIG_HEX);
  const [decoded, setDecoded] = useState<DecodedStep[]>([]);
  const [error, setError] = useState('');
  const [hasDecoded, setHasDecoded] = useState(false);

  const handleDecode = () => {
    setError('');
    const cleaned = hexInput.replace(/\s+/g, '');
    if (!cleaned) {
      setError('Please enter a hex string to decode.');
      setDecoded([]);
      setHasDecoded(true);
      return;
    }
    if (!/^[0-9a-fA-F]*$/.test(cleaned)) {
      setError('Invalid hex string. Only characters 0-9 and a-f are allowed.');
      setDecoded([]);
      setHasDecoded(true);
      return;
    }
    if (cleaned.length % 2 !== 0) {
      setError('Invalid hex string. Length must be even (each byte is 2 hex characters).');
      setDecoded([]);
      setHasDecoded(true);
      return;
    }
    const steps = decodeScriptSig(cleaned);
    if (steps.length === 0) {
      setError('No script operations could be parsed from this hex string.');
      setDecoded([]);
    } else {
      setDecoded(steps);
    }
    setHasDecoded(true);
  };

  const resetToGenesis = () => {
    setHexInput(GENESIS_SCRIPTSIG_HEX);
    setDecoded([]);
    setError('');
    setHasDecoded(false);
  };

  const asciiMessages = decoded
    .filter((step) => step.ascii && /^[\x20-\x7e]+$/.test(step.ascii) && step.ascii.length > 3)
    .map((step) => step.ascii);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <header className="relative overflow-hidden border-b border-border">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-background to-background pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent pointer-events-none" />

        <div className="container relative py-12 md:py-20">
          {/* Image */}
          <div className="flex justify-center mb-8">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary to-primary/60 rounded-2xl blur-md opacity-50 group-hover:opacity-75 transition-opacity" />
              <img
                src="https://raw.githubusercontent.com/Marc26z/bitcoiners/refs/heads/main/assets/jan3(1).jpg"
                alt="January 3rd Bitcoin Genesis Block - The Times headline"
                className="relative rounded-xl shadow-2xl max-w-full h-auto max-h-[400px] object-contain border border-border/50"
              />
            </div>
          </div>

          {/* Title */}
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-4">
              <span className="text-primary">Fun</span>{' '}
              <span className="text-foreground">With Freedom Tech</span>
            </h1>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-primary mb-6">
              Decoder
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Decode the scriptSig from the Bitcoin Genesis Block &mdash; the very first
              block mined by Satoshi Nakamoto on January 3, 2009.
            </p>
          </div>
        </div>
      </header>

      {/* Decoder Section */}
      <section className="container py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          {/* Input */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <label
                htmlFor="scriptsig-input"
                className="text-sm font-semibold uppercase tracking-wider text-muted-foreground"
              >
                ScriptSig Hex
              </label>
              <button
                onClick={resetToGenesis}
                className="text-xs text-primary hover:text-primary/80 transition-colors underline underline-offset-2"
              >
                Reset to Genesis Block
              </button>
            </div>
            <textarea
              id="scriptsig-input"
              value={hexInput}
              onChange={(e) => {
                setHexInput(e.target.value);
                setHasDecoded(false);
                setError('');
                setDecoded([]);
              }}
              placeholder="Paste your scriptSig hex here..."
              rows={4}
              className="w-full px-4 py-3 bg-card border border-border rounded-lg font-mono text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all resize-y"
              spellCheck={false}
            />
            <button
              onClick={handleDecode}
              className="mt-4 w-full sm:w-auto px-8 py-3 bg-primary text-primary-foreground font-semibold rounded-lg hover:bg-primary/90 active:bg-primary/80 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-background"
            >
              Decode ScriptSig
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-8 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
              <p className="text-sm text-destructive font-medium">{error}</p>
            </div>
          )}

          {/* Results */}
          {decoded.length > 0 && (
            <div className="space-y-6">
              {/* Human-readable message */}
              {asciiMessages.length > 0 && (
                <div className="p-6 bg-primary/10 border border-primary/30 rounded-xl">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-primary mb-3">
                    Message
                  </h3>
                  <blockquote className="text-xl md:text-2xl font-serif italic text-foreground leading-relaxed border-l-4 border-primary pl-4 py-1">
                    &ldquo;{asciiMessages.join(' | ')}&rdquo;
                  </blockquote>
                </div>
              )}

              {/* Step-by-step breakdown */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                  Step-by-Step Breakdown
                </h3>
                <div className="space-y-2">
                  {decoded.map((step, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 p-4 bg-card border border-border rounded-lg hover:border-primary/30 transition-colors"
                    >
                      <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary/20 text-primary text-xs font-bold">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="inline-block px-2 py-0.5 bg-secondary text-secondary-foreground rounded text-xs font-mono font-semibold mb-2">
                          {step.label}
                        </span>
                        {step.hex && (
                          <div className="mt-2 space-y-1">
                            <div className="text-xs text-muted-foreground uppercase tracking-wider">
                              Hex
                            </div>
                            <code className="block text-xs font-mono text-foreground/80 break-all bg-secondary/50 rounded px-3 py-2">
                              {step.hex}
                            </code>
                          </div>
                        )}
                        {step.ascii && (
                          <div className="mt-2 space-y-1">
                            <div className="text-xs text-muted-foreground uppercase tracking-wider">
                              ASCII
                            </div>
                            <code className="block text-sm font-mono text-primary break-all bg-primary/5 rounded px-3 py-2">
                              {step.ascii}
                            </code>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Empty state after decode with no results */}
          {hasDecoded && decoded.length === 0 && !error && (
            <div className="p-8 border border-dashed border-border rounded-xl text-center">
              <p className="text-muted-foreground">
                No script operations could be parsed. Try pasting the Genesis block scriptSig hex.
              </p>
            </div>
          )}

          {/* About section */}
          {!hasDecoded && (
            <div className="mt-12 p-6 bg-card border border-border rounded-xl">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                About the Genesis Block
              </h3>
              <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                <p>
                  On <strong className="text-foreground">January 3, 2009</strong>, Satoshi Nakamoto
                  mined the first Bitcoin block &mdash; Block #0, known as the{' '}
                  <strong className="text-foreground">Genesis Block</strong>.
                </p>
                <p>
                  Embedded in its coinbase transaction&apos;s scriptSig is a reference to the
                  headline from <em>The Times</em> newspaper that day:{' '}
                  <span className="text-primary font-medium">
                    &ldquo;The Times 03/Jan/2009 Chancellor on brink of second bailout for
                    banks&rdquo;
                  </span>
                  .
                </p>
                <p>
                  This serves as both a timestamp (proving the block could not have been created
                  before January 3, 2009) and a political statement about the flaws of the
                  fractional-reserve banking system that Bitcoin was designed to offer an
                  alternative to.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container text-center text-xs text-muted-foreground space-y-2">
          <p>
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors underline underline-offset-2"
            >
              Vibed with Shakespeare and Marc's 🍝 code.
            </a>
          </p>
          <p>
            Fun With Freedom Tech &mdash; Learning Bitcoin, one block at a time.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
